import React from 'react';
import PropTypes from 'prop-types';

import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Error from '../../components/Error';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';

import * as assetSelectors from '../../selectors/assetSelectors';
import * as newAssetSelectors from '../../selectors/newAssetSelectors';
import * as assetsConstants from '../../constants/assets';
import * as bucketSelectors from '../../selectors/bucketSelectors';

import OtherApplicationsPaths from '../../helpers/OtherApplicationsPaths';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';

class AddBucketAssetDialog extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            title: '',
            assetEditType: 'wizard',
            isIframeLoaded: false,
        };
    }

    componentDidMount() {
        if (this.props.asset === 'Chart') {
            window.addEventListener('message', this.onHandleIframeEvents);

            if (this.state.isIframeLoaded) {
                this.setState({ isIframeLoaded: false });
            }
        }
    }

    componentDidUpdate({ loading: wasLoading }) {
        const { loading, error } = this.props;
        if (wasLoading && !loading && !error) {
            this.onClose();
        }
    }

    componentWillUnmount() {
        if (this.props.asset === 'Chart') {
            window.removeEventListener('message', this.onHandleIframeEvents);
        }
    }

    onHandleIframeEvents = e => {
        if (e.data === 'close') {
            this.onClose();
        }

        if (typeof e.data === 'object' && e.data.id) {
            this.props.addCreatedAsset(e.data.id);
        }

        if (e.data === 'bulkProjectCreated') {
            this.onClose();
            setTimeout(() => {
                window.location.reload();
            }, 1000);
        }

    };

    onTitleChange = event => {
        this.setState({
            title: event.target.value,
        });
    };

    onEnterCreateNew = event => {
        if (event && event.key === 'Enter') {
            const { title } = this.state;
            if (title !== '') {
                this.onCreateNew();
            }
        }
    };

    onCreateNew = () => {
        const { asset, createNewAsset } = this.props;
        const { title, assetEditType } = this.state;

        if (asset === 'Survey' || asset === 'Map') {
            createNewAsset(title, assetEditType);
        } else {
            createNewAsset(title);
        }
    };

    onClose = () => {
        const { toggleDialog, clearErrorMessage } = this.props;

        toggleDialog();
        clearErrorMessage();
        this.setState({ title: '', isIframeLoaded: false });
    };

    onIframeLoaded = () => {
        // Waits a bit before showing the iframe to prevent graphical glitches
        setTimeout(() => {
            this.setState({ isIframeLoaded: true });
        }, 1000);
    };

    render() {
        const {
            asset,
            loading,
            error,
            classes,
            isDialogOpen,
            toggleDialog,
            bucket,
        } = this.props;

        if (!isDialogOpen) {
            return null;
        }

        const { title } = this.state;
        const isFormDisabled = title === '';
        let createButton, cancelButton, loadingIndicator;

        if (loading) {
            loadingIndicator = <CircularProgress />;
        } else {
            cancelButton = <Button onClick={this.onClose}>CANCEL</Button>;
            createButton = (
                <Button
                    onClick={this.onCreateNew}
                    color="primary"
                    variant="contained"
                    disabled={isFormDisabled}
                    data-testid="create-widget-button"
                >
                    CREATE {asset.toUpperCase()}
                </Button>
            );
        }

        if (asset === 'Chart') {
            const chartCreationUrl = OtherApplicationsPaths.apps.chart.create.getPath(
                {
                    id: bucket.id,
                },
            );

            const iframeLoadingIndicator = (
                <div className={classes.iframeLoadingContainer} data-testid="iframe-spinner">
                    <CircularProgress size={150} />
                </div>
            );

            return (
                <Dialog
                    open={isDialogOpen}
                    onClose={toggleDialog}
                    fullWidth={true}
                    maxWidth="md"
                    data-testid="chart-create-modal"
                >
                    {!this.state.isIframeLoaded && iframeLoadingIndicator}
                    <iframe
                        title="Chart Creation"
                        className={classes.iframe}
                        data-testid="chart-creation-iframe"
                        src={chartCreationUrl}
                        onLoad={this.onIframeLoaded}
                    />
                </Dialog>
            );
        }

        return (
            <Dialog open={isDialogOpen} onClose={toggleDialog}>
                <DialogTitle>Create new {asset}</DialogTitle>
                <DialogContent>
                    <div className={classes.content}>
                        <TextField
                            required
                            label="Title"
                            value={title}
                            onChange={this.onTitleChange}
                            onKeyPress={this.onEnterCreateNew}
                            autoFocus
                            inputProps={{
                                'data-testid': 'create-widget-text-input',
                            }}
                        />
                        <Error>{error}</Error>
                        {loadingIndicator}
                    </div>
                </DialogContent>
                <DialogActions>
                    {cancelButton}
                    {createButton}
                </DialogActions>
            </Dialog>
        );
    }
}

const mapStateToProps = state => {
    return {
        asset: assetSelectors.getApplicationAsset(state),
        error: newAssetSelectors.getError(state),
        loading: newAssetSelectors.getLoading(state),
        bucket: bucketSelectors.getBucket(state),
    };
};

const mapDispatchToProps = dispatch => ({
    createNewAsset: (title, assetEditType) => {
        dispatch({
            type: assetsConstants.NEW_ASSET_REQUEST,
            payload: {
                title,
                assetEditType,
            },
        });
    },
    addCreatedAsset: id => {
        dispatch({
            type: assetsConstants.NEW_ASSET_REQUEST,
            payload: {
                project: { id },
                isAlreadyCreated: true,
            },
        });
    },
    clearErrorMessage: () => {
        dispatch({
            type: assetsConstants.NEW_ASSET_CLEAR_ERROR,
        });
    },
});

AddBucketAssetDialog.propTypes = {
    asset: PropTypes.string,
    loading: PropTypes.bool,
    error: PropTypes.string,
    createNewAsset: PropTypes.func.isRequired,
    addCreatedAsset: PropTypes.func,
    clearErrorMessage: PropTypes.func.isRequired,
    isDialogOpen: PropTypes.bool.isRequired,
    toggleDialog: PropTypes.func.isRequired,
    bucket: PropTypes.object,
};

AddBucketAssetDialog.defaultProps = {
    loading: false,
    error: undefined,
};

const style = ({ spacing }) => ({
    content: {
        display: 'flex',
        flexDirection: 'column',
    },
    iframe: {
        width: '960px',
        height: '760px',
        border: 'none',
    },
    iframeLoadingContainer: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: 'white',
    },
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(injectSheet(style)(AddBucketAssetDialog));



// WEBPACK FOOTER //
// ./src/containers/bucketAssets/AddBucketAssetDialog.js